import React, { useEffect } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useChat } from "@/hooks/useChat";
import { useSearchParams } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import ChatList from "@/components/messages/ChatList";
import ChatHeader from "@/components/messages/ChatHeader";
import MessageList from "@/components/messages/MessageList";
import MessageInput from "@/components/messages/MessageInput";
import DeleteMessageDialog from "@/components/messages/DeleteMessageDialog";
import DeleteChatDialog from "@/components/messages/DeleteChatDialog";
import { Skeleton } from "@/components/ui/skeleton";

const Messages = () => {
  const {
    user,
    isAuthenticated,
    chats,
    isLoadingChats,
    selectedChat,
    setSelectedChat,
    newMessage,
    setNewMessage,
    otherUserProfile,
    currentListing,
    showDeleteAlert,
    setShowDeleteAlert,
    confirmChatDelete,
    setConfirmChatDelete,
    messages,
    isLoadingMessages,
    selectedImage,
    setSelectedImage,
    isUploading,
    sendMessageMutation,
    handleSendMessage,
    clearSelectedImage,
    handleDeleteMessage,
    confirmDeleteMessage,
    handleDeleteChat,
    confirmDeleteChat,
    formatMessageDate,
    formatChatDate,
    handleBackToList,
    handleViewListing,
  } = useChat();

  const [searchParams] = useSearchParams();

  // Auto select first chat if none selected
  useEffect(() => {
    if (!isLoadingChats && chats.length > 0 && !searchParams.get('chat') && !selectedChat) {
      if (chats[0] && chats[0].id) {
        setSelectedChat(chats[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingChats, chats, searchParams, selectedChat]);

  if (!isAuthenticated || !user) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow pt-16 bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <p>Ju lutemi identifikohuni për të parë mesazhet.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      {/* Adjusted main to add padding-bottom on mobile (pb-14 for 56px) and remove it on md screens (md:pb-0) */}
      <main className="flex-grow pt-16 bg-gray-50 dark:bg-gray-900 pb-14 md:pb-0">
        {/* FLEX CONTAINER für die ganze Chat-Oberfläche */}
        <div className="container mx-auto px-0 md:px-4 py-0 md:py-8 flex flex-col min-h-[calc(100svh-64px)] md:h-auto md:min-h-0"> {/* Adjusted md:min-h-0 to md:h-auto for clarity, effect is similar with flex parents */}
          <div className="bg-white dark:bg-gray-800 rounded-none md:rounded-lg shadow-sm overflow-hidden border-gray-200 dark:border-gray-700 flex flex-1 min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-3 flex-1 min-h-0">
              {/* Chat List */}
              <div className={`col-span-1 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 
                  overflow-y-auto min-h-0
                  ${selectedChat ? "hidden md:block" : "block"}`}>
                <ChatList
                  chats={chats}
                  isLoading={isLoadingChats}
                  selectedChatId={selectedChat}
                  onSelectChat={setSelectedChat}
                  handleDeleteChat={handleDeleteChat}
                  formatChatDate={formatChatDate}
                  currentUserId={user.id}
                />
              </div>
              {/* Chat Box */}
              <div className={`md:col-span-2 flex flex-col min-h-0 ${selectedChat ? 'flex' : 'hidden md:flex'}`}>
                {selectedChat && otherUserProfile ? (
                  <div className="flex flex-col flex-1 min-h-0">
                    {/* Mobile: Back-Button */}
                    <div className="md:hidden flex items-center border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <button
                        onClick={() => setSelectedChat(null)}
                        className="p-2 focus:outline-none text-gray-600 dark:text-gray-300"
                        aria-label="Kthehu te lista"
                      >
                        <svg width={24} height={24} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="ml-2 font-bold">Biseda</span>
                    </div>
                    <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 hidden md:block">
                      <ChatHeader
                        otherUserProfile={otherUserProfile}
                        currentListing={currentListing}
                        handleBackToList={handleBackToList}
                        handleDeleteChat={handleDeleteChat}
                        selectedChatId={selectedChat}
                        handleViewListing={handleViewListing}
                      />
                    </div>
                    {/* MessageList container: Removed overflow-y-auto, p-3/md:p-4, space-y. Kept flex-grow, min-h-0, and bg colors. */}
                    <div className="flex-grow bg-gray-50 dark:bg-gray-850 min-h-0">
                      <MessageList
                        messages={messages}
                        isLoading={isLoadingMessages}
                        formatMessageDate={formatMessageDate}
                        handleDeleteMessage={handleDeleteMessage}
                        currentUserId={user.id}
                      />
                    </div>
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-2 md:p-3 bg-white dark:bg-gray-800">
                      <MessageInput
                        newMessage={newMessage}
                        setNewMessage={setNewMessage}
                        selectedImage={selectedImage}
                        setSelectedImage={setSelectedImage}
                        isUploading={isUploading}
                        sendMessageMutation={sendMessageMutation}
                        handleSendMessage={handleSendMessage}
                        clearSelectedImage={clearSelectedImage}
                      />
                    </div>
                  </div>
                ) : isLoadingChats || (selectedChat && isLoadingMessages) ? (
                  <div className="flex flex-col h-full justify-center items-center p-4">
                    <Skeleton className="h-12 w-12 rounded-full mb-4" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : (
                  <div className="flex flex-col h-full justify-center items-center text-center p-4">
                    <MessageCircle size={48} className="text-gray-400 dark:text-gray-500 mb-4" />
                    <p className="text-gray-700 dark:text-gray-300 text-lg">Zgjidhni një bisedë</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Ose filloni një të re nga një shpallje.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <DeleteMessageDialog
        showDeleteAlert={showDeleteAlert}
        setShowDeleteAlert={setShowDeleteAlert}
        confirmDeleteMessage={confirmDeleteMessage}
      />
      <DeleteChatDialog
        confirmChatDelete={confirmChatDelete}
        setConfirmChatDelete={setConfirmChatDelete}
        confirmDeleteChat={confirmDeleteChat}
      />
      {/* Footer is part of the document flow, pb-14 on main will push it down or provide space above a fixed bottom nav */}
      <Footer />
    </div>
  );
};

export default Messages;
